import { ref, computed, type InjectionKey } from 'vue';
import { getFundedDiscountOffers } from '../services/api';
import { type FundedDiscount } from '../types';
import RootStoreHelpers from '@monolith/legacy/store/helpers';
import { captureLoyaltyException } from '@core/plugins/sentry/helper';
import { isEnabled, FeatureFlag } from '@monolith/legacy/services/features';

export interface FundedDiscountsContext {
  getOffer: (brandUuid: string) => FundedDiscount | undefined;
  hasOffer: (brandUuid: string) => boolean;
}

export const FUNDED_DISCOUNTS_KEY = Symbol('fundedDiscounts') as InjectionKey<FundedDiscountsContext>;

export function useFundedDiscounts() {
  const { userIsRetailer } = RootStoreHelpers.useGetters([
    'userIsRetailer',
  ]);

  const offers = ref<FundedDiscount[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const hasBeenLoaded = ref(false);

  let ongoingFetchPromise: Promise<FundedDiscount[]> | null = null;
  let loadedUuidsKey: string | null = null;

  const ensureLoaded = (brandUuids: string[]): Promise<FundedDiscount[]> => {
    if (!isEnabled(FeatureFlag.FundedDiscounts)) {
      offers.value = [];
      return Promise.resolve(offers.value);
    }

    if (!userIsRetailer.value) {
      offers.value = [];
      return Promise.resolve(offers.value);
    }

    const key = [...brandUuids].sort((a, b) => a.localeCompare(b)).join(',');

    if (hasBeenLoaded.value && loadedUuidsKey === key) {
      return Promise.resolve(offers.value);
    }

    if (ongoingFetchPromise !== null && loadedUuidsKey === key) {
      return ongoingFetchPromise;
    }

    loadedUuidsKey = key;
    isLoading.value = true;
    error.value = null;

    ongoingFetchPromise = getFundedDiscountOffers(brandUuids)
      .then((response) => {
        const data = response?.data ?? [];
        offers.value = data;
        hasBeenLoaded.value = true;
        return data;
      })
      .catch((err) => {
        error.value = (err as Error).message || 'Failed to load funded discounts';
        captureLoyaltyException(err, [
          { label: 'component', value: 'funded-discounts/composable' },
          { label: 'action', value: 'ensureLoaded' },
        ]);
        ongoingFetchPromise = null;
        throw err;
      })
      .finally(() => {
        isLoading.value = false;
      });

    return ongoingFetchPromise;
  };

  const isLoaded = computed(() => hasBeenLoaded.value);

  const getOffer = (brandUuid: string): FundedDiscount | undefined => {
    return offers.value.find(
      (offer) => offer.attributes.brandUuid === brandUuid
    );
  };

  const hasOffer = (brandUuid: string): boolean => {
    return offers.value.some(
      (offer) => offer.attributes.brandUuid === brandUuid
    );
  };

  return {
    offers,
    isLoading,
    error,
    isLoaded,

    ensureLoaded,
    getOffer,
    hasOffer,
  };
}
